import React, { useContext } from 'react';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo } from '@fortawesome/pro-regular-svg-icons';
import './OfficeFloorPlan.scss';

import { OfficeContext } from './Office';
import { getSmartImage } from '../../helpers/helpers';
import {
  isRoomType,
  SPACE_TYPE_LABELS,
  canBookSpace,
  getSpaceBookingsAtTime,
  isSpaceAvailable,
  OFFICE_DOOR_STATES,
  formatTimeClean
} from '../../helpers/office_helpers';
import OfficeSpacePopover from './OfficeSpacePopover';
import { Tooltip } from 'react-tippy';

const OfficeFloorPlan = ({ spaces }) => {
  const ctx = useContext(OfficeContext);
  const {
    selectedFloor,
    selectedSpace,
    setSelectedSpace,
    currentUser,
    selectedDate,
    selectedTime,
    selectedEndTime,
    focusedUserId,
    focusedUserSchedule
  } = ctx;

  if (!selectedFloor?.image_url) {
    return (
      <div className='office-floor-plan-outer empty'>
        <div className='empty-message'>No floor plan image uploaded yet.</div>
      </div>
    );
  }

  const windowStart = `${selectedDate}T${selectedTime}:00`;
  const windowEnd = selectedEndTime ? `${selectedDate}T${selectedEndTime}:00` : `${selectedDate}T${selectedTime}:30`;

  const handleContainerClick = e => {
    if (e.target === e.currentTarget || e.target.classList.contains('floor-plan-image')) {
      setSelectedSpace(null);
    }
  };

  return (
    <div className='office-floor-plan-outer'>
      <div className='floor-plan-container' onClick={handleContainerClick}>
        <img src={getSmartImage(selectedFloor.image_url)} alt={selectedFloor.name} className='floor-plan-image' />

        {spaces.map(space => {
          if (space.floor_plan_x == null) return null;
          const activeBookings = getSpaceBookingsAtTime(space, windowStart, windowEnd);
          const hasBookings = activeBookings.length > 0;
          const firstBooking = hasBookings ? activeBookings[0] : null;
          const userCanBook = canBookSpace(currentUser, space);
          const available = isSpaceAvailable(space, windowStart, windowEnd);
          const isSelected = selectedSpace?.id === space.id;
          const isRoom = isRoomType(space);
          const isDeskType = space.type === 'desk' || space.type === 'open_seating';
          const bookingLabel = isDeskType && firstBooking?.title ? firstBooking.title : firstBooking?.user?.name || 'Booked';
          const doorLocked = hasBookings && firstBooking?.door_state && firstBooking.door_state !== OFFICE_DOOR_STATES.OPEN;

          const allDayBookings = (space.bookings || []).filter(b => b.status === 'confirmed');
          const userInSpace = focusedUserId && allDayBookings.some(b => b.User_id === focusedUserId);
          const userDimmed = focusedUserId && !userInSpace;
          const focusedSpaceBookings = userInSpace ? focusedUserSchedule.filter(b => b.OfficeSpace_id === space.id || b.space?.id === space.id) : [];
          const zoomParticipants = ctx.zoomParticipants?.[space.id];

          return (
            <div
              key={space.id}
              className={cn('floor-plan-space', {
                booked: hasBookings,
                available: !hasBookings && userCanBook,
                locked: !userCanBook,
                'door-locked': doorLocked,
                selected: isSelected,
                label: space.type === 'label',
                room: isRoom,
                desk: space.type === 'desk',
                'phone-booth': space.type === 'phone_booth',
                'user-dimmed': userDimmed,
                'user-highlighted': userInSpace
              })}
              style={{
                left: `${space.floor_plan_x * 100}%`,
                top: `${space.floor_plan_y * 100}%`,
                width: `${(space.floor_plan_w || 0.06) * 100}%`,
                height: `${(space.floor_plan_h || 0.06) * 100}%`
              }}
              onClick={e => {
                e.stopPropagation();
                setSelectedSpace(isSelected ? null : space);
              }}
              title={`${space.name}${hasBookings ? ` — ${bookingLabel}` : ''}`}
            >
              {space.type === 'desk' && <div className='space-label'>{space.name.split('-').at(-1) || ''}</div>}
              {space.type !== 'desk' && <div className='space-label'>{space.name}</div>}

              {hasBookings &&
                !zoomParticipants &&
                firstBooking?.user &&
                !(isDeskType && firstBooking?.title) &&
                (firstBooking.user.image ? (
                  <img src={getSmartImage(firstBooking.user.image)} alt='' className='space-avatar' />
                ) : (
                  <div className='space-avatar space-monogram'>{firstBooking.user.name[0]}</div>
                ))}

              {Boolean(zoomParticipants) && !doorLocked && (
                <ul className='floor-zoom-participants zoom-participants-list'>
                  {zoomParticipants.map(p => {
                    const teamMember = ctx.allUsers.find(u => u.email === p);
                    return (
                      <li key={p} className='zoom-participant'>
                        {teamMember ? (
                          <img src={getSmartImage(teamMember.image)} className='zoom-avatar'></img>
                        ) : (
                          <p className='zoom-monogram'>{p[0]}</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {hasBookings && activeBookings.length > 1 && <div className='space-count'>+{activeBookings.length}</div>}

              {!userCanBook && !doorLocked && <div className='space-lock'>🔒</div>}

              {isRoom && !hasBookings && userCanBook && space.persistent_zoom_room_url && (
                <>
                  <div className='space-join-indicator' />
                  <div className='space-video-icon'>
                    <FontAwesomeIcon icon={faVideo} />
                  </div>
                </>
              )}

              {!focusedUserId && userCanBook && !available && hasBookings && isRoom && <div className='space-hover-action booked-action'>Booked</div>}
              {!focusedUserId && !userCanBook && <div className='space-hover-action'>Unavailable</div>}

              {focusedSpaceBookings.length > 0 && (
                <div className='user-schedule-badge'>
                  {focusedSpaceBookings.map(b => (
                    <div key={b.id} className='badge-entry'>
                      <span className='badge-time'>
                        {formatTimeClean(b.starts_at)} – {formatTimeClean(b.ends_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Inline popover for selected space */}
        {selectedSpace && selectedSpace.floor_plan_x != null && <OfficeSpacePopover space={selectedSpace} onClose={() => setSelectedSpace(null)} />}
      </div>
    </div>
  );
};

export default OfficeFloorPlan;
